/**
 * Lightweight SEO helper — no dependency. Google executes JS, so
 * client-side title/meta/JSON-LD updates are indexed. Non-JS crawlers
 * (iMessage/Facebook shares) get static OG from index.html or the
 * backend share pages (/api/share/listing/:id).
 */
import { useEffect } from "react";
import { BRAND } from "@/lib/constants";

const SITE = "https://whyteglove.vip";
const DEFAULT_TITLE = "The Whyte Glove · Luxury NY Real Estate — Lamar A. Whyte";

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(id, data) {
  const key = `jsonld-${id}`;
  let el = document.getElementById(key);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = key;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function agentJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    name: BRAND.agentName,
    url: SITE,
    telephone: BRAND.phoneDirect,
    address: {
      "@type": "PostalAddress",
      streetAddress: "401 Park Ave South, 10th Fl",
      addressLocality: "New York",
      addressRegion: "NY",
      postalCode: "10016",
      addressCountry: "US",
    },
    areaServed: ["The Bronx", "Brooklyn", "Queens", "Manhattan", "Westchester County"],
    parentOrganization: { "@type": "Organization", name: "Real Broker NY LLC" },
  };
}

export function listingJsonLd(p) {
  if (!p) return null;
  const offer = {
    "@type": "Offer",
    price: p.price || undefined,
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
  };
  return {
    "@context": "https://schema.org",
    "@type": "Residence",
    name: p.title || p.address,
    address: p.address,
    numberOfRooms: p.bedrooms || undefined,
    url: `${SITE}/listings/${p.id}`,
    image: Array.isArray(p.images) && p.images.length ? p.images[0] : undefined,
    offers: offer,
  };
}

export default function Seo({ title, description, path, jsonLd, jsonLdId = "page" }) {
  useEffect(() => {
    const fullTitle = title ? `${title} — The Whyte Glove` : DEFAULT_TITLE;
    document.title = fullTitle;
    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    if (path) {
      upsertMeta("property", "og:url", `${SITE}${path}`);
      upsertLink("canonical", `${SITE}${path}`);
    }
    setJsonLd(jsonLdId, jsonLd || null);
    return () => setJsonLd(jsonLdId, null);
  }, [title, description, path, jsonLd, jsonLdId]);
  return null;
}
